import React, { useEffect, useState } from "react";

/**
 * Countdown renders a live ticking countdown to a target date.
 * Props:
 *   - target: ISO string or Date (UTC date is fine)
 *   - compact: if true, smaller layout (one-line)
 *   - testIdPrefix: optional prefix for data-testid attributes
 */
function diff(target) {
  const t = +new Date(target) - Date.now();
  if (t <= 0) return { done: true, d: 0, h: 0, m: 0, s: 0 };
  return {
    done: false,
    d: Math.floor(t / 86400000),
    h: Math.floor((t / 3600000) % 24),
    m: Math.floor((t / 60000) % 60),
    s: Math.floor((t / 1000) % 60),
  };
}

const pad = (n) => String(n).padStart(2, "0");

export default function Countdown({ target, compact = false, testIdPrefix = "countdown" }) {
  const [now, setNow] = useState(() => diff(target));

  useEffect(() => {
    const id = setInterval(() => setNow(diff(target)), 1000);
    return () => clearInterval(id);
  }, [target]);

  if (now.done) {
    return (
      <div data-testid={`${testIdPrefix}-done`} className="font-display-tight text-[#FACC15] text-xl">
        🎉 It’s live!
      </div>
    );
  }

  if (compact) {
    return (
      <span
        data-testid={`${testIdPrefix}-compact`}
        className="inline-flex items-center gap-1 font-display-tight tabular-nums"
      >
        <span className="text-[#FACC15] font-black">{now.d}d</span>
        <span className="text-white/70">·</span>
        <span className="text-white/90">{pad(now.h)}h</span>
        <span className="text-white/70">·</span>
        <span className="text-white/90">{pad(now.m)}m</span>
        <span className="text-white/70">·</span>
        <span className="text-white/90">{pad(now.s)}s</span>
      </span>
    );
  }

  const cell = (val, lbl, key) => (
    <div key={key} className="flex flex-col items-center min-w-[3.5rem] sm:min-w-[4.5rem]">
      <div
        data-testid={`${testIdPrefix}-${lbl.toLowerCase()}`}
        className="font-display tabular-nums text-4xl sm:text-5xl md:text-6xl leading-none text-white"
      >
        {pad(val)}
      </div>
      <div className="mt-1.5 text-[9px] sm:text-[10px] font-black tracking-[0.25em] uppercase text-[#FACC15]">
        {lbl}
      </div>
    </div>
  );

  return (
    <div
      data-testid={testIdPrefix}
      className="inline-flex items-stretch gap-2 sm:gap-3 bg-[#0A1128] border-2 border-[#FACC15]/30 rounded-2xl px-4 sm:px-6 py-4 sm:py-5"
    >
      {cell(now.d, "Days", "d")}
      <div className="text-3xl sm:text-4xl text-[#FACC15]/30 self-center">:</div>
      {cell(now.h, "Hrs", "h")}
      <div className="text-3xl sm:text-4xl text-[#FACC15]/30 self-center">:</div>
      {cell(now.m, "Min", "m")}
      <div className="text-3xl sm:text-4xl text-[#FACC15]/30 self-center">:</div>
      {cell(now.s, "Sec", "s")}
    </div>
  );
}
